import { useCallback, useEffect, useState } from "react";
import { EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export function LaunchAnnouncementCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/launch-announcement").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const pending = data.recipients.filter((r) => !r.already_sent);
  const done = data.recipients.filter((r) => r.already_sent);

  const testSend = async () => {
    setBusy("test");
    try {
      const r = await api.post("/admin/launch-announcement/test");
      toast.success(`Test sent to ${r.data.to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const send = async () => {
    setBusy("send");
    try {
      const r = await api.post("/admin/launch-announcement/send", { confirm: true }, { timeout: 300000 });
      toast.success(`Sent to ${r.data.sent.length} client${r.data.sent.length === 1 ? "" : "s"}` +
        (r.data.failed.length ? ` — ${r.data.failed.length} failed` : ""));
      setConfirmed(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mb-8 rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="launch-announcement-card">
      <div className="border-b border-stone-100 px-6 py-5">
        <p className="font-heading text-lg font-semibold text-stone-900">Launch announcement email</p>
        <p className="mt-1 text-xs leading-relaxed text-stone-500">
          "An Update from Silver Home Care" — goes to active clients with an email on file (or their representative's email).
          Currently {data.recipients.length} of {data.active_clients} active clients have an email on file.
        </p>
      </div>

      <div className="px-6 py-5">
        <div className="overflow-hidden rounded-lg border border-stone-200" data-testid="launch-announcement-preview"
          dangerouslySetInnerHTML={{ __html: data.html }} />
      </div>

      <div className="px-6 pb-5">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-400">Recipients</p>
        {data.recipients.length === 0 ? (
          <p className="rounded-lg bg-amber-50 px-4 py-3 text-xs text-amber-800" data-testid="launch-no-recipients">
            No active clients have an email on file yet. Add client or representative emails to their records first —
            otherwise this announcement has nowhere to go.
          </p>
        ) : (
          <div className="mb-3 flex flex-wrap gap-1.5" data-testid="launch-recipient-list">
            {data.recipients.map((r) => (
              <span key={r.client_id} title={r.email}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${r.already_sent ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-stone-200 bg-stone-50 text-stone-600"}`}
                data-testid={`launch-recipient-${r.client_id}`}>
                {r.name}{r.via === "representative" ? " (via rep)" : ""}{r.already_sent ? " · sent" : ""}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={testSend} disabled={!!busy}
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-stone-300 px-4 text-xs font-semibold text-stone-700 transition-colors hover:bg-stone-900 hover:text-white disabled:opacity-40"
            data-testid="launch-test-send">
            <EnvelopeSimple size={15} /> {busy === "test" ? "Sending…" : "Send a test to my inbox"}
          </button>
          <label className="flex items-center gap-2 text-xs text-stone-600">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="launch-confirm-checkbox" />
            I reviewed the email and the recipient list
          </label>
          <button onClick={send} disabled={!!busy || !confirmed || pending.length === 0}
            className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-xs font-semibold text-primary-foreground transition-colors hover:bg-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="launch-send-button">
            <PaperPlaneTilt size={15} /> {busy === "send" ? "Sending…" : `Send to ${pending.length} client${pending.length === 1 ? "" : "s"}`}
          </button>
          {done.length > 0 && <span className="text-[11px] text-stone-400" data-testid="launch-sent-count">{done.length} already sent</span>}
        </div>
      </div>
    </div>
  );
}
